import { useState, useEffect, useRef, useCallback } from 'react'
import { embedDashboard } from '@superset-ui/embedded-sdk'
import { PageHeader, colors } from '../../components/ui'
import { api } from '../../services/api'

interface DashboardEntry {
  readonly id: string
  readonly name: string
  readonly supersetId: string
}

const tabStyle = (active: boolean): React.CSSProperties => ({
  padding: '8px 16px',
  border: 'none',
  borderBottom: active ? `2px solid ${colors.accent}` : '2px solid transparent',
  background: 'none',
  cursor: 'pointer',
  fontWeight: active ? 600 : 400,
  color: active ? colors.accent : colors.text,
  fontSize: '0.875rem',
})

export function ReportsPage(): JSX.Element {
  const [dashboards, setDashboards] = useState<readonly DashboardEntry[]>([])
  const [activeDashboard, setActiveDashboard] = useState<DashboardEntry | null>(null)
  const [error, setError] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    api
      .get<readonly DashboardEntry[]>('/superset/dashboards')
      .then((res) => {
        const list = res.data ?? []
        setDashboards(list)
        if (list.length > 0) setActiveDashboard(list[0])
      })
      .catch(() => setError('Failed to load dashboards'))
  }, [])

  const fetchGuestToken = useCallback(async () => {
    if (!activeDashboard) throw new Error('No dashboard selected')
    const res = await api.post<{ guestToken: string }>('/superset/guest-token', {
      dashboardId: activeDashboard.supersetId,
    })
    return res.data?.guestToken ?? ''
  }, [activeDashboard])

  useEffect(() => {
    if (!activeDashboard?.supersetId || !containerRef.current) return
    const container = containerRef.current
    container.innerHTML = ''

    embedDashboard({
      id: activeDashboard.supersetId,
      supersetDomain: `${window.location.origin}/superset`,
      mountPoint: container,
      fetchGuestToken,
      dashboardUiConfig: {
        hideTitle: true,
        hideChartControls: false,
        hideTab: false,
      },
    })
  }, [activeDashboard, fetchGuestToken])

  const hasTabs = dashboards.length > 1
  const noDashboards = dashboards.length === 0 && !error
  const noSupersetId = activeDashboard && !activeDashboard.supersetId

  return (
    <div>
      <PageHeader title="Reports & Analytics" />

      {hasTabs && (
        <div style={{ display: 'flex', gap: '4px', borderBottom: `1px solid ${colors.border}`, marginBottom: '1rem' }}>
          {dashboards.map((d) => (
            <button key={d.id} onClick={() => setActiveDashboard(d)} style={tabStyle(activeDashboard?.id === d.id)}>
              {d.name}
            </button>
          ))}
        </div>
      )}

      {error && <div style={{ color: '#dc2626', padding: '0.5rem 0' }}>{error}</div>}

      {noDashboards && <p style={{ color: colors.textMuted, padding: '2rem 0' }}>No dashboards configured yet.</p>}

      {noSupersetId && (
        <div style={{ padding: '2rem', color: colors.textMuted, textAlign: 'center' }}>
          <p>Dashboard &ldquo;{activeDashboard.name}&rdquo; is not yet connected to Superset.</p>
          <p style={{ marginTop: '0.5rem' }}>
            Create the dashboard in{' '}
            <a href="/superset/" target="_blank" rel="noopener noreferrer" style={{ color: colors.accent }}>
              Superset
            </a>
            , then update the dashboard ID in the API configuration.
          </p>
        </div>
      )}

      <div
        ref={containerRef}
        className="superset-embed-container"
        style={{
          width: '100%',
          height: activeDashboard?.supersetId ? 'calc(100vh - 160px)' : undefined,
          border: activeDashboard?.supersetId ? `1px solid ${colors.border}` : undefined,
          borderRadius: '4px',
        }}
      />
      <style>{`
        .superset-embed-container iframe {
          width: 100% !important;
          height: 100% !important;
          min-height: calc(100vh - 160px);
          border: none !important;
        }
      `}</style>
    </div>
  )
}
